import DOMPurify from 'dompurify';
import map from 'lodash/map';
import flatten from 'lodash/flatten';

export const allowedTagsDefault = ['b', 'sub', 'sup', 'hi', 'i', 'br', 'span', 'strong'];
export const allowedTagsExtendedDefault = [
  'p',
  'hi',
  'i',
  'br',
  'span',
  'b',
  'u',
  'strong',
  'em',
  'mark',
  'small',
  'del',
  'ins',
  'sub',
  'sup',
  'ul',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];
export const tagsToRemoveDefault = ['script', 'style', 'iframe', 'link'];
export const allowedAttributesDefault = [];

export function unicodeHtmlEntity(entity) {
  const hex = entity.replace(/[&#x;]/g, '');
  return String.fromCharCode(parseInt(hex, 16));
}

export function unicodeHtmlEntities(input) {
  if (!input) return input;
  return input.replace(/&#x[a-fA-F0-9]+;/gm, (match) => unicodeHtmlEntity(match));
}
/*
 * allowedTags will be rendered
 * tagsToRemove will be removed completely
 * all other tags will be escaped
 */
export function sanitizeEscape(
  input,
  { allowedTags = allowedTagsDefault, allowedAttributes = allowedAttributesDefault, tagsToRemove = tagsToRemoveDefault } = {}
) {
  // Add hook for escaping
  DOMPurify.addHook('afterSanitizeElements', (node) => {
    if ((node instanceof Element || node instanceof HTMLDocument) && !allowedTags.includes(node.tagName.toLowerCase())) {
      const newNode = document.createTextNode(node.outerHTML);
      node.parentNode.insertBefore(newNode, node);
      node.remove();
    }
  });

  const sanitized = DOMPurify.sanitize(input, {
    ADD_TAGS: allowedTags,
    FORBID_TAGS: tagsToRemove,
    ALLOWED_ATTR: allowedAttributes,
  });

  // Remove hook
  DOMPurify.removeHooks('afterSanitizeElements');

  return sanitized;
}

/*
 * allowedTags will be rendered
 * everything else will be escaped, including custom tag-like string, e.g. 3-<Difluor-methoxy>-benzoesaeure
 */
export function sanitizeEscapeCustom(input, { allowedTags = allowedTagsDefault } = {}) {
  // Make allowed tags array lower case
  const allowedTagsLowercase = allowedTags.map((c) => c.toLowerCase());

  // Output is the input, edited
  let output = input;

  // Two-step approach to avoid ReDoS:
  // Step 1 — find tag boundaries using a single negated character class ([^>]*).
  //   A negated class with one quantifier cannot backtrack catastrophically: it
  //   advances linearly until it finds ">" or exhausts the input.
  // Step 2 — extract the tag name from the already-bounded match string using a
  //   start-anchored pattern with no adjacent overlapping quantifier.
  // eslint-disable-next-line sonarjs/slow-regex -- [^>]* is a single negated character class; no adjacent overlapping quantifier; backtracking is strictly linear
  const tagBoundaryReg = /<[^>]*>/g;

  // An array that will contain all disallowed tags
  const disallowedTags = [];

  // For each tag in the input, if it's allowed, skip
  // Else, add it to the array.
  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = tagBoundaryReg.exec(input)) !== null) {
    const nameMatch = /^<\/?([a-zA-Z0-9]*)/.exec(match[0]);
    const tagName = nameMatch?.[1] ?? '';
    // eslint-disable-next-line no-continue
    if (allowedTagsLowercase.includes(tagName.toLowerCase())) continue;
    disallowedTags.push(match[0]);
  }

  // Replace each disallowed tag with the "escaped" version
  disallowedTags.forEach((tag) => {
    const find = tag;
    const replace = tag.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    output = output.replace(find, replace);
  });

  return output;
}

/*
 * allowedTags will be rendered
 * all other tags will be discarded
 */
export function sanitizeDiscard(input, { allowedTags = allowedTagsDefault, allowedAttributes = allowedAttributesDefault } = {}) {
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttributes,
  });
}

export function sanitize(input, mode, options) {
  switch (mode) {
    case 'escape': {
      return sanitizeEscape(input, options);
    }
    case 'escapeCustom': {
      return sanitizeEscapeCustom(input, options);
    }
    case 'discard':
    default: {
      return sanitizeDiscard(input, options);
    }
  }
}

const HTML_EXCEPTIONS = ['sub', 'sup', 'i', 'hi'];

function generateSplitRegexp(tagSet) {
  const regexp = map(tagSet, (tag) => ['<', tag, '>[^<]+</', tag, '>'].join(''));
  regexp.push('.');
  return new RegExp(regexp.join('|'), 'g');
}

const _splitRegexp = generateSplitRegexp(HTML_EXCEPTIONS);

/*
 * Removes sub/sup/i/hi tags only, leaving all other content (including other HTML) as-is.
 * Equivalent to the deprecated HtmlStripsService.getTitleWithoutHtml(str, 'stripexceptions').
 */
export function stripExceptionTags(input) {
  if (!input) return input;
  let result = input;
  HTML_EXCEPTIONS.forEach((tag) => {
    const reg = new RegExp(`(<\\/?${tag}[^>]*)>`, 'gm');
    result = result.replace(reg, '');
  });
  return result;
}

function wrapEachSymbol(str, openTag, closeTag) {
  return map(str.split(''), (val) => [openTag, val, closeTag].join(''));
}

function shatterMatches(matches = []) {
  const results = [];
  matches.forEach((match) => {
    if (match === '<') {
      results.push('&lt');
      return;
    }
    // eslint-disable-next-line sonarjs/slow-regex -- [^>]+ and [^<]+ are negated character classes; backtracking is linear, not catastrophic
    const parsed = /(<[^>]+>)([^<]+)(<\/[^>]+>)/.exec(match);
    if (parsed) {
      results.push(wrapEachSymbol(parsed[2], parsed[1], parsed[3]));
    } else {
      results.push(match);
    }
  });
  return flatten(results);
}

/*
 * Splits HTML text into two parts of (total - lastSymbols) and lastSymbols characters,
 * preserving HTML tags. Equivalent to the deprecated HtmlStripsService.splitHtmlText.
 */
export function splitHtmlText(htmlText, lastSymbols) {
  const trimmed = htmlText.trim();
  const result = { first: null, last: null, full: htmlText };

  // eslint-disable-next-line sonarjs/slow-regex -- [^>]+ is a negated character class bounded by its delimiter; no catastrophic backtracking possible
  if (!/<[^>]+>/g.test(trimmed)) {
    result.first = trimmed.substr(0, trimmed.length - lastSymbols);
    result.last = trimmed.substr(-lastSymbols);
    return result;
  }

  const match = shatterMatches(trimmed.match(_splitRegexp));
  result.first = match.slice(0, match.length - lastSymbols)?.join('');
  result.last = match.slice(match.length - lastSymbols)?.join('');
  return result;
}
